import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Crown,
  Shield,
  ArrowRight,
  Lock,
  Globe,
  TrendingUp,
  Users,
  Clock,
  X,
} from 'lucide-react';
import InvestmentAssessment from './InvestmentAssessment';

const benefits = [
  {
    icon: Globe,
    title: 'Off-Market Access',
    description: 'Properties never listed publicly',
  },
  {
    icon: TrendingUp,
    title: 'Market Intelligence',
    description: 'Data-driven investment insights',
  },
  {
    icon: Users,
    title: 'Private Network',
    description: 'Exclusive seller relationships',
  },
  {
    icon: Clock,
    title: '24-Hour Response',
    description: 'Priority client handling',
  },
];

interface ExclusiveAccessCTAProps {
  variant?: 'section' | 'floating';
}

const ExclusiveAccessCTA = ({ variant = 'section' }: ExclusiveAccessCTAProps) => {
  const [showAssessment, setShowAssessment] = useState(false);

  if (variant === 'floating') {
    return (
      <>
        {/* Floating CTA trigger */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-24 right-6 z-40"
        >
          <Button
            onClick={() => setShowAssessment(true)}
            className="bg-gradient-gold text-cream hover:opacity-90 shadow-xl shadow-gold/20 rounded-full px-6 py-3 h-auto"
          >
            <Crown className="h-4 w-4 mr-2" />
            Start Assessment
          </Button>
        </motion.div>

        {/* Assessment Modal */}
        <AnimatePresence>
          {showAssessment && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-charcoal/80 backdrop-blur-sm z-50"
                onClick={() => setShowAssessment(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-x-4 top-[5vh] bottom-[5vh] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl z-50 overflow-y-auto"
              >
                <div className="relative">
                  <button
                    onClick={() => setShowAssessment(false)}
                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/80 border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <InvestmentAssessment variant="modal" onComplete={() => setShowAssessment(false)} />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <section id="investment-assessment" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Premium background */}
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/95 to-charcoal" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/10 via-transparent to-transparent" />
      
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A86C' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left column - Value proposition */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
              <Crown className="h-4 w-4 text-gold" />
              <span className="text-sm font-medium text-gold tracking-wider uppercase">
                Private Client Services
              </span>
            </div>

            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-cream mb-6">
              Investment Strategy
              <span className="block italic font-medium text-gold">Tailored to You</span>
            </h2>

            <p className="text-cream/70 text-lg mb-8 max-w-lg">
              Complete our 2-minute investment assessment and receive personalized property
              recommendations matched to your objectives, timeline, and investment criteria.
            </p>

            {/* Benefits grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="h-5 w-5 text-gold" />
                  </div>
                  <div>
                    <p className="font-medium text-cream text-sm">{benefit.title}</p>
                    <p className="text-xs text-cream/50">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 pt-6 border-t border-cream/10">
              <div className="flex items-center gap-2 text-cream/50 text-sm">
                <Shield className="h-4 w-4" />
                <span>100% Confidential</span>
              </div>
              <div className="flex items-center gap-2 text-cream/50 text-sm">
                <Lock className="h-4 w-4" />
                <span>Data Protected</span>
              </div>
            </div>
          </motion.div>

          {/* Right column - Assessment widget */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <InvestmentAssessment />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ExclusiveAccessCTA;
